import { Check, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { chipAffordanceClasses, chipSizeClasses } from "@/components/Shared/TagChip/chip.styles";
import { CompetenceItem } from "@/models/Competence";
import { cn } from "@/lib/utils";
import { filterArchivedCompetences } from "@/utils/competence.utils";
import { CompetenceFilter } from "./jobConsultantPanel.utils";

interface CompetencePathPopoverProps {
	/** The org competence tree (branch → service → skills), unfiltered. */
	competences: CompetenceItem[];
	/** The current constraint; undefined = all competences. */
	value?: CompetenceFilter;
	onChange: (value?: CompetenceFilter) => void;
	/** The job's own competence — its service row gets a ✨ marker. */
	jobCompetence?: CompetenceFilter;
	/** Label shown in the trigger pill (resolved by the caller). */
	triggerLabel: string;
}

const checkboxClasses = (checked: boolean) =>
	cn(
		"flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
		checked ? "border-brand bg-brand text-white" : "border-gray-300 bg-white"
	);

/**
 * The competence constraint as a compact dropdown: the current path renders as
 * a clickable pill, opening a nested tree of the org taxonomy. Matching runs on
 * ONE branch/service path, so services select exclusively (picking one replaces
 * the path) while the skills of the selected service toggle as checkboxes.
 * "Alla kompetenser" clears the constraint entirely.
 */
const CompetencePathPopover: React.FC<CompetencePathPopoverProps> = ({
	competences,
	value,
	onChange,
	jobCompetence,
	triggerLabel,
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [expandedBranch, setExpandedBranch] = useState<string | undefined>(value?.branch);

	const tree = filterArchivedCompetences(competences);

	function handleOpenChange(next: boolean) {
		setOpen(next);
		// Land with the current selection's branch unfolded
		if (next) setExpandedBranch(value?.branch);
	}

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<button
					type="button"
					title={t("consultantPanel.changeCompetence") as string}
					// Deliberately the CertificationChip recipe: this trigger stands in
					// the same filter row as the certification/tag chips, so it borrows
					// their shell, size classes and 20px trailing affordance to line up
					// at the same height and font size
					className={cn(
						"inline-flex max-w-full items-center truncate rounded border border-solid border-border-strong bg-white font-medium text-gray-900 hover:bg-gray-100",
						chipSizeClasses("sm")
					)}
				>
					<span className="truncate">{triggerLabel}</span>
					<span className={chipAffordanceClasses}>
						<ChevronDown size={13} className="opacity-60" />
					</span>
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-80 p-0">
				<div className="max-h-80 overflow-y-auto p-2">
					{/* Clear the constraint */}
					<button
						type="button"
						role="radio"
						aria-checked={!value}
						onClick={() => {
							onChange(undefined);
							setOpen(false);
						}}
						className="flex w-full items-center gap-2 rounded border-0 bg-transparent px-1 py-1.5 text-left text-sm hover:bg-gray-50"
					>
						<span className={checkboxClasses(!value)}>
							{!value && <Check size={12} strokeWidth={3} />}
						</span>
						<span className="min-w-0 flex-1 truncate">{t("consultantPanel.allCompetences")}</span>
					</button>

					{tree.map((branch) => {
						const branchOpen = expandedBranch === branch.key;
						const branchSelected = value?.branch === branch.key;
						return (
							<React.Fragment key={branch.key}>
								<button
									type="button"
									onClick={() => setExpandedBranch(branchOpen ? undefined : branch.key)}
									className="flex w-full items-center gap-1.5 rounded border-0 bg-transparent px-1 py-1.5 text-left text-sm font-medium text-gray-800 hover:bg-gray-50"
								>
									{branchOpen ? (
										<ChevronDown size={14} className="shrink-0 text-gray-500" />
									) : (
										<ChevronRight size={14} className="shrink-0 text-gray-500" />
									)}
									<span className={cn("min-w-0 flex-1 truncate", branchSelected && "text-brand")}>
										{branch.name.sv ?? branch.key}
									</span>
								</button>

								{branchOpen &&
									(branch.items ?? []).map((service) => {
										const isSelected = branchSelected && value?.service === service.key;
										const isJobService =
											jobCompetence?.branch === branch.key &&
											jobCompetence?.service === service.key;
										return (
											<React.Fragment key={service.key}>
												<button
													type="button"
													role="radio"
													aria-checked={isSelected}
													onClick={() =>
														onChange({ branch: branch.key, service: service.key, skills: [] })
													}
													className="flex w-full items-center gap-2 rounded border-0 bg-transparent py-1.5 pl-6 pr-1 text-left text-sm hover:bg-gray-50"
												>
													<span className={checkboxClasses(isSelected)}>
														{isSelected && <Check size={12} strokeWidth={3} />}
													</span>
													<span className="min-w-0 flex-1 truncate">
														{service.name.sv ?? service.key}
													</span>
													{isJobService && (
														<Sparkles
															size={12}
															className="shrink-0 opacity-70"
															aria-label={t("consultantPanel.jobCompetenceMarker") as string}
														/>
													)}
												</button>

												{/* Skills toggle under the SELECTED service only */}
												{isSelected &&
													(service.items ?? []).map((skill) => {
														const skillSelected = !!value?.skills.includes(skill.key);
														return (
															<button
																key={skill.key}
																type="button"
																role="checkbox"
																aria-checked={skillSelected}
																onClick={() =>
																	onChange({
																		...value!,
																		skills: skillSelected
																			? value!.skills.filter((key) => key !== skill.key)
																			: [...value!.skills, skill.key],
																	})
																}
																className="flex w-full items-center gap-2 rounded border-0 bg-transparent py-1 pl-12 pr-1 text-left text-sm text-gray-700 hover:bg-gray-50"
															>
																<span className={checkboxClasses(skillSelected)}>
																	{skillSelected && <Check size={12} strokeWidth={3} />}
																</span>
																<span className="min-w-0 flex-1 truncate">
																	{skill.name.sv ?? skill.key}
																</span>
															</button>
														);
													})}
											</React.Fragment>
										);
									})}
							</React.Fragment>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default CompetencePathPopover;
